import {Component, OnInit} from '@angular/core';
import {ValidatedForm} from "../../../forms/validated-form";
import {FormBuilder, Validators} from "@angular/forms";
import {Institution, InstOrder, InstService} from "../../../services/inst.service";
import {Router} from "@angular/router";
import {UtilService} from "../../../services/util.service";
import {StateService} from "../../../services/state.service";
import {Constants} from "../../../util/constants";
import {OrderService} from "../../../services/order.service";
import {finalize} from "rxjs/operators";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {Utils} from "../../../util/utils";
import {AsyncValidatorFactoryService} from "../../../services/async-validator-factory.service";

const MIN_ORDER_COUNT = 1;
const TEST_KIT_SKU: string = 'covid19_test_kit';

@Component({
    templateUrl: './inst-order.component.html',
    styleUrls: ['./inst-order.component.css']
})
export class InstOrderComponent extends ValidatedForm implements OnInit {

    inst: Institution;
    initializing: boolean;
    promoExpires: boolean;
    today: string;
    tomorrow: string;
    timeZone: string;
    order: InstOrder;
    action: string;

    constructor(protected fb:FormBuilder,
                private instService: InstService,
                private router: Router,
                private utilService: UtilService,
                private orderService: OrderService,
                private asyncValidatorService: AsyncValidatorFactoryService,
                private state: StateService) {
        super(fb);
        this.inst = this.state.get('institution');
        if (!this.inst) {
            this.router.navigateByUrl('/').then();
            return;
        }
        this.order = this.state.get('inst_order');
        const promoLen = this.inst.promoPrefix.length;
        this.action = this.order? "Edit order" : "New order";

        this.timeZone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
        if (!this.timeZone) {
            const gmtOffset:number = -new Date().getTimezoneOffset()/60;
            this.timeZone = gmtOffset < 0? `GMT${gmtOffset}` : `GMT+${gmtOffset}`;
        }

        this.today = new Date().toISOString().substr(0,10);
        // add 1 day for no good reason, but otherwise, we get yesterday as first allowed date
        let now = new Date(Date.now() + 86400000);
        this.tomorrow = now.toISOString().substr(0,10);

        let promoExpDate = this.tomorrow;
        let promoExpTime = '00:00';
        if (this.order && this.order.promo_expires) {
            this.promoExpires = true;
            const expWhen = new Date(this.order.promo_expires);
            promoExpDate = Utils.AsIsoDate(expWhen);
            promoExpTime = Utils.AsIsoTimestamp(expWhen).substr(-5);
        }

        this.initForm({
            promoCode: {
                value: this.order? this.order.promo_code : this.inst.promoPrefix,
                validators: [Validators.minLength(promoLen+1)],
                asyncValidators: [this.asyncValidatorService.getPromoCodeAvailableValidator(null, this.order?.promo_code)],
                messages: {minlength: "Must append something to prefix", codeTaken: "Code is already in use"}
            },
            promoExpDate: {
                value: promoExpDate,
                validators: [Validators.required],
                messages: {required: Constants.ERR_REQUIRED}
            },
            promoExpTime: { value: promoExpTime },
            unitCount: {
                value: this.order? this.order.unit_count : '',
                validators: [Validators.min(MIN_ORDER_COUNT), Validators.required],
                messages: {min: `Must order at least ${MIN_ORDER_COUNT}`, required: Constants.ERR_REQUIRED}
            },
            unitPrice: {
                value: this.order? this.order.unit_price : '',
                validators: [Validators.required],
                messages: {required: Constants.ERR_REQUIRED}
            },
            discount: { value: this.order? this.order.discount : ''},
            discountDesc: { value: this.order? this.order.discount_description : '' },
            charges: { value: this.order? this.order.charges : '' },
            chargesDesc: { value: this.order? this.order.charges_description : '' },
            notes: { value: this.order? this.order.notes : '' },
            paid: { value: this.order? !!this.order.paid : '' },
            paymentInfo: { value: this.order? this.order.payment_info : '' }
        });
        this.enableDisableExpiration();
        this.enableDisablePayment();
        this.disableDueToPayment();
    }

    ngOnInit() {
        // If we have an order, we don't need to initialize unit price to default
        if (this.order)
            return;
        this.initializing = true;
        this.orderService.getProduct(TEST_KIT_SKU).pipe(
            finalize(() => {this.initializing=false})
        ).subscribe((p) => {
            this.form.patchValue({unitPrice: p.price});
        }, this.utilService.getErrorHandler("Error retrieving product info"));
    }

    /** If we're editing a prior order and it was already paid, disable changes to all important fields
     */
    disableDueToPayment() {
        if (!!this.order?.paid) {
            const controls = this.form.controls;
            controls.promoCode.disable();
            controls.unitCount.disable();
            controls.unitPrice.disable();
            controls.discount.disable();
            controls.charges.disable();
            controls.paid.disable();
        }
    }

    displayCreated(): string {
        return this.order? Utils.AsUsTimestamp(this.order.created) : '';
    }

    expiresChanged(ev: MatCheckboxChange) {
        this.promoExpires = ev.checked;
        this.enableDisableExpiration();
    }

    private enableDisableExpiration() {
        if (this.promoExpires) {
            this.form.controls.promoExpDate.enable();
            this.form.controls.promoExpTime.enable();
        } else {
            this.form.controls.promoExpDate.disable();
            this.form.controls.promoExpTime.disable();
        }
    }

    private enableDisablePayment() {
        if (this.fields.paid.value)
            this.form.controls.paymentInfo.enable();
        else
            this.form.controls.paymentInfo.disable();
    }

    protected formValueChanged(data: any) {
        super.formValueChanged(data);
        this.enableDisablePayment()
    }

    private numVal(fieldName:string): number {
        const sValue: string = this.fields[fieldName]?.value;
        return sValue? +sValue : 0;
    }


    get total(): number {
        const total = this.numVal('unitCount') * this.numVal('unitPrice')
                    - this.numVal('discount') + this.numVal('charges');
        return Math.max(Utils.RoundToCents(total), 0);
    }

    codeChanged() {
        const control = this.form.controls.promoCode;
        let text:string = control.value;
        // First remove any spaces
        text = text.replace(/\s/g,'');
        if (!text.startsWith(this.inst.promoPrefix))
            text = this.inst.promoPrefix;
        text = text.toUpperCase();
        this.form.patchValue({promoCode: text});
    }

    submit() {
        if (this.order)
            this.update();
        else
            this.create();
    }

    update() {
        const controls = this.form.controls;
        // Figure out which fields changed and collect their values. Always include ID field anyway.
        let changedOrder: InstOrder = { id: this.order.id };

        if (controls.promoCode.dirty && this.fields.promoCode.value.toUpperCase().trim() != this.order.promo_code)
            changedOrder.promo_code = this.fields.promoCode.value;

        let totalChanged: boolean = false;
        if (controls.unitCount.dirty) {
            changedOrder.unit_count = this.numVal('unitCount');
            totalChanged = true;
        }
        if (controls.unitPrice.dirty) {
            changedOrder.unit_price = this.numVal('unitPrice');
            totalChanged =true;
        }
        if (controls.discount.dirty) {
            changedOrder.discount = this.numVal('discount');
            totalChanged = true;
        }
        if (controls.charges.dirty) {
            changedOrder.charges = this.numVal('charges');
            totalChanged = true;
        }
        if (totalChanged)
            changedOrder.total = this.total;

        if (this.promoExpires && (!this.order.promo_expires || controls.promoExpDate.dirty || controls.promoExpTime.dirty))
            changedOrder.promo_expires = this.buildExpiresFromFields();
        else if (!this.promoExpires && this.order.promo_expires)
            changedOrder.promo_expires = null;

        if (controls.discountDesc.dirty)
            changedOrder.discount_description = this.fields.discountDesc.value;
        if (controls.chargesDesc.dirty)
            changedOrder.charges_description = this.fields.chargesDesc.value;
        if (controls.notes.dirty)
            changedOrder.notes = this.fields.notes.value;

        if (this.fields.paid.value && (!this.order.payment_info || controls.paymentInfo.dirty))
            changedOrder.payment_info = this.fields.paymentInfo.value;
        else if (!this.fields.paid.value && this.order.payment_info)
            changedOrder.payment_info = null;

        this.submitting = true
        this.instService.updateOrder(this.inst.id, changedOrder).pipe(
            finalize(() => {this.submitting = false})
        ).subscribe((info) => {
            this.router.navigateByUrl('/institution/orders').then();
        }, this.utilService.getErrorHandler("Error updating order"));
    }

    create() {
        const order: InstOrder = {
            promo_code: this.fields.promoCode.value,
            unit_count: this.fields.unitCount.value,
            unit_price: this.numVal('unitPrice'),
            total: this.total,
        };

        if (this.fields.discount.value) {
            order.discount = this.numVal('discount');
            order.discount_description = this.fields.discountDesc.value;
        }

        if (this.fields.charges.value) {
            order.charges = this.numVal('charges');
            order.charges_description = this.fields.chargesDesc.value;
        }

        if (this.fields.notes.value)
            order.notes = this.fields.notes.value;

        if (this.fields.paid.value)
            order.payment_info = this.fields.paymentInfo.value || 'paid';

        // If promo expires, pass a proper timestamp with time zone string describing the expiration time, in a format
        // appropriate for use in PostgreSQL database
        if (this.promoExpires)
            order.promo_expires = this.buildExpiresFromFields();

        this.submitting = true
        this.instService.addOrder(this.inst.id, order).pipe(
            finalize(() => {this.submitting = false})
        ).subscribe((info) => {
            this.router.navigateByUrl('/institution/orders').then();
        }, this.utilService.getErrorHandler("Error creating order"));
    }

    /** Takes the info from date and time fields and builds a proper timestamp with time zone string describing the
     * expiration time, in a format that PosgreSQL can parse.
     *
     * @return the formatted timestamp
     */
    private buildExpiresFromFields(): string {
        const date = Utils.AsIsoDate(this.fields.promoExpDate.value);
        let time = this.fields.promoExpTime.value;
        // If it's in AM/PM format, convert to 24-hour hh:mm
        if (time.endsWith("M")) {
            const m = time.match(/(\d+):(\d+) (\S)M/);
            if (m) {
                let hour = +m[1];
                const minute = m[2];
                if (m[3] == 'P')
                    hour += 12;
                time = `${Utils.ZeroPad(hour, 2)}:${minute}`;
            }
        }
        return `${date}T${time} ${this.timeZone}`;
    }

    canSave(): boolean {
        return this.form.valid && !this.submitting;
    }

    cancel() {
        this.router.navigateByUrl('/institution/orders').then();
    }
}
